import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { useLeague } from '../contexts/LeagueContext';
import { useTheme } from '../contexts/ThemeContext';
import { useParams, useNavigate } from 'react-router-dom';
import type { UserDashboardModel } from '@/models/UserDashboardModels';
import type { LeagueModel } from '@/models/LeagueModels';
import LoadingSpinner from '../components/LoadingSpinner';
import CodeOfConductModal from '../components/CodeOfConductModal';
import LeagueComponent from '../components/League';
import usePageTitle from '@/hooks/usePageTitle';
import { resolveType } from '@/services/ServiceContainer';
import { UserDashboardService } from '@/services/UserDashboardService';
import { Timestamp } from 'firebase/firestore';
import { UserService } from '@/services/UserService';
import { RoundService } from '@/services/RoundService';

// Add weather icon mapping
const weatherIcons: Record<number, string> = {
  0: '☀️', // Clear
  1: '🌤️', // Mainly clear
  2: '⛅', // Partly cloudy
  3: '☁️', // Overcast
  45: '🌫️', // Fog
  48: '🌫️', // Depositing rime fog
  51: '🌦️', // Drizzle: Light
  53: '🌦️', // Drizzle: Moderate
  55: '🌦️', // Drizzle: Dense
  56: '🌧️', // Freezing Drizzle: Light
  57: '🌧️', // Freezing Drizzle: Dense
  61: '🌧️', // Rain: Slight
  63: '🌧️', // Rain: Moderate
  65: '🌧️', // Rain: Heavy
  66: '🌧️', // Freezing Rain: Light
  67: '🌧️', // Freezing Rain: Heavy
  71: '🌨️', // Snow fall: Slight
  73: '🌨️', // Snow fall: Moderate
  75: '🌨️', // Snow fall: Heavy
  77: '🌨️', // Snow grains
  80: '🌦️', // Rain showers: Slight
  81: '🌦️', // Rain showers: Moderate
  82: '🌦️', // Rain showers: Violent
  85: '🌨️', // Snow showers: Slight
  86: '🌨️', // Snow showers: Heavy
  95: '⛈️', // Thunderstorm: Slight/Moderate
  96: '⛈️', // Thunderstorm with hail: Slight
  99: '⛈️', // Thunderstorm with hail: Heavy
};

const Dashboard: React.FC = () => {
  const { user: firebaseUser } = useAuth();
  const { appUser } = useUser();
  const { leagues, loading: leaguesLoading } = useLeague();
  const { theme } = useTheme();
  const { dayId } = useParams<{ dayId?: string }>();
  const navigate = useNavigate();
  const [userDashboardModel, setUserDashboardModel] = useState<UserDashboardModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [codeOfConductLeague, setCodeOfConductLeague] = useState<LeagueModel | null>(null);
  const [declinedLeagues, setDeclinedLeagues] = useState<Set<string>>(new Set());
  // Weather state
  const [weather, setWeather] = useState<null | {
    temperature: number;
    weathercode: number;
    sunset: string;
  }>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState<string | null>(null);
  // Current time state for weather section
  const [currentTime, setCurrentTime] = useState(() => new Date());

  // Set page title
  usePageTitle('Dashboard');

  // Handle redirection when no dayId is provided
  useEffect(() => {
    if (!dayId && !leaguesLoading && Object.keys(leagues).length > 0) {
      // Find the best dayId to redirect to
      let bestDayId: string | null = null;

      // First, try to find an active day
      for (const league of Object.values(leagues)) {
        const activeSchedule = league.schedule.find(schedule => schedule.status === 'active');
        if (activeSchedule) {
          bestDayId = activeSchedule.dayId;
          break;
        }
      }

      // If no active day, find the closest future day
      if (!bestDayId) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (const league of Object.values(leagues)) {
          for (const schedule of league.schedule) {
            const [year, month, day] = schedule.dayId.split('_').slice(1); // Remove 'day_' prefix
            const scheduleDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            if (scheduleDate >= today) {
              bestDayId = schedule.dayId;
              break;
            }
          }
          if (bestDayId) break;
        }
      }

      // If still no dayId found, use the first available day
      if (!bestDayId && Object.values(leagues).length > 0) {
        const firstLeague = Object.values(leagues)[0];
        if (firstLeague.schedule.length > 0) {
          bestDayId = firstLeague.schedule[0].dayId;
        }
      }

      // Redirect to the best dayId
      if (bestDayId) {
        navigate(`/dashboard/${bestDayId}`, { replace: true });
      }
    }
  }, [dayId, leaguesLoading, leagues, navigate]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!firebaseUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userDashboardService = resolveType(UserDashboardService);
        const userDashboardModel = await userDashboardService.get(firebaseUser.uid);

        if (userDashboardModel) {
          setUserDashboardModel(userDashboardModel);
        } else {
          // Create a new userDashboard document if it doesn't exist
          // console.log('Creating new userDashboard document on Dashboard page');
          const newUserDashboardModel: UserDashboardModel = {
            userId: firebaseUser.uid,
            updatedAt: Timestamp.now(),
            casualRounds: [],
            leagueRounds: {},
            tournamentRounds: [],
          };

          await userDashboardService.create(newUserDashboardModel);
          setUserDashboardModel(newUserDashboardModel);
        }

        setError(null);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [firebaseUser]);

  // Check for leagues with unaccepted code of conduct
  useEffect(() => {
    if (appUser && Object.keys(leagues).length > 0 && appUser.activeLeagues) {
      // console.log('Checking for leagues with unaccepted code of conduct');
      // console.log('Active leagues:', leagues);
      // console.log('User activeLeagues:', appUser.activeLeagues);
      // console.log('Declined leagues:', declinedLeagues);

      // Find the first league that needs code of conduct acceptance
      const leagueNeedingAcceptance = Object.values(leagues).find(league => {
        // Skip if the user has already declined this league's code of conduct
        if (declinedLeagues.has(league.id)) {
          return false;
        }

        // Check if the user has accepted the code of conduct for this league
        const leagueSummary = appUser.activeLeagues[league.id];
        if (!leagueSummary) {
          // console.log(`No league summary found for league ${league.id}`);
          return false;
        }

        // If codeOfConductAcceptedDate is null, the user hasn't accepted yet
        return !leagueSummary.codeOfConductAcceptedDate;
      });

      if (leagueNeedingAcceptance) {
        // console.log('Found league needing code of conduct acceptance:', leagueNeedingAcceptance);
        setCodeOfConductLeague(leagueNeedingAcceptance);
      } else {
        // console.log('No leagues need code of conduct acceptance');
        setCodeOfConductLeague(null);
      }
    } else {
      setCodeOfConductLeague(null);
    }
  }, [appUser, leagues, declinedLeagues]);

  const handleCodeOfConductDecline = (leagueId: string) => {
    setDeclinedLeagues(prev => new Set([...prev, leagueId]));
    setCodeOfConductLeague(null);
  };

  const handleCodeOfConductAccept = async (leagueId: string) => {
    if (!firebaseUser) return;

    try {
      const userService = resolveType(UserService);
      const userModel = await userService.get(firebaseUser.uid);

      if (userModel) {
        const updatedActiveLeagues = { ...userModel.activeLeagues };

        if (updatedActiveLeagues[leagueId]) {
          updatedActiveLeagues[leagueId] = {
            ...updatedActiveLeagues[leagueId],
            codeOfConductAcceptedDate: Timestamp.now()
          };

          await userService.update(userModel.uid, {
            activeLeagues: updatedActiveLeagues,
            updatedAt: Timestamp.now()
          });

          // Update the local appUser state
          if (appUser) {
            appUser.activeLeagues = updatedActiveLeagues;
          }

          // console.log('Updated user.activeLeagues:', updatedActiveLeagues);
        }
      }
    } catch (error) {
      console.error('Error updating code of conduct acceptance:', error);
    }

    setCodeOfConductLeague(null);
  };

  useEffect(() => {
    // Granger, IA coordinates
    const lat = 41.7617;
    const lon = -93.8227;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&daily=sunset&timezone=America/Chicago`;
    setWeatherLoading(true);
    fetch(url)
      .then(res => res.json())
      .then(data => {
        setWeather({
          temperature: data.current_weather.temperature,
          weathercode: data.current_weather.weathercode,
          sunset: data.daily.sunset[0],
        });
        setWeatherLoading(false);
      })
      .catch(err => {
        setWeatherError('Could not load weather: ' + err);
        setWeatherLoading(false);
      });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Sync localStorage currentRound with Firestore
    const loadActiveRound = async () => {
      const roundService = resolveType(RoundService);
      if (userDashboardModel?.activeRoundId) {
        const roundModel = await roundService.get(userDashboardModel!.activeRoundId!);
        if (roundModel) {
          // if (roundModel.status !== 'active') {
          //   // 

          // } else {
          //   setActiveRound(roundModel);
          // }
        }
      }
    };

    loadActiveRound();
  }, []);

  // const sectionStyle = {
  //   backgroundColor: theme.colors.SURFACE,
  //   padding: theme.spacing.MEDIUM,
  //   borderRadius: '8px',
  //   marginBottom: theme.spacing.LARGE,
  //   border: `1px solid ${theme.colors.BORDER}`,
  // };

  // const headerStyle = {
  //   color: theme.colors.TEXT,
  //   marginBottom: theme.spacing.MEDIUM,
  //   fontSize: theme.fonts.LARGE,
  // };

  // const cardStyle = {
  //   backgroundColor: theme.colors.BACKGROUND,
  //   padding: theme.spacing.MEDIUM,
  //   borderRadius: '4px',
  //   marginBottom: theme.spacing.SMALL,
  //   display: 'flex',
  //   justifyContent: 'space-between',
  //   alignItems: 'center',
  //   border: `1px solid ${theme.colors.BORDER}`,
  // };

  // const linkStyle = {
  //   color: theme.colors.PRIMARY,
  //   textDecoration: 'none',
  //   fontWeight: 'bold',
  // };

  if (loading || leaguesLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: theme.colors.BACKGROUND,
          color: theme.colors.ERROR,
          textAlign: 'center',
          padding: theme.spacing.XLARGE,
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: theme.colors.BACKGROUND,
        color: theme.colors.TEXT,
        padding: 0,
        margin: 0,
        width: '100%',
        position: 'relative'
      }}
    >
      {/* Code of Conduct Modal */}
      {codeOfConductLeague && (
        <CodeOfConductModal
          leagueId={codeOfConductLeague.id}
          leagueName={codeOfConductLeague.name}
          codeOfConduct={codeOfConductLeague.codeOfConduct || ''}
          userId={firebaseUser?.uid || ''}
          onClose={() => setCodeOfConductLeague(null)}
          onAccept={() => handleCodeOfConductAccept(codeOfConductLeague.id)}
          onDecline={() => handleCodeOfConductDecline(codeOfConductLeague.id)}
        />
      )}

      <style>
        {`
          .card:hover {
            transform: translateY(-2px);
            transition: transform 0.2s ease;
          }
        `}
      </style>

      {/* Active Leagues Section */}
      {Object.keys(leagues).length > 0 && (
        <div style={{ width: '100%', margin: 0, padding: 0 }}>
          {Object.values(leagues).map((league) => (
            <LeagueComponent
              key={league.id}
              league={league}
              userDashboard={userDashboardModel}
              initialDayId={dayId}
            />
          ))}
        </div>
      )}

      {/* Weather and sunset info at the bottom */}
      <div style={{
        width: '100%',
        marginTop: 48,
        padding: '32px 0 24px 0',
        background: `linear-gradient(90deg, ${theme.colors.SURFACE} 0%, ${theme.colors.BACKGROUND} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderTop: `2px solid ${theme.colors.BORDER}`,
      }}>
        <div style={{ fontSize: '1.2em', color: theme.colors.TEXT, fontWeight: 600, marginBottom: 8 }}>
          Granger, IA — {currentTime.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })}
        </div>
        {weatherLoading ? (
          <div style={{ fontSize: '1.5em', color: theme.colors.TEXT_SECONDARY }}>Loading weather…</div>
        ) : weatherError ? (
          <div style={{ fontSize: '1.5em', color: theme.colors.ERROR }}>{weatherError}</div>
        ) : weather ? (
          <>
            <div style={{ fontSize: '3.5em', marginBottom: 8 }}>
              {weatherIcons[weather.weathercode] || '❓'}
            </div>
            <div style={{ fontSize: '2.2em', fontWeight: 700, marginBottom: 4 }}>
              {Math.round(weather.temperature * 9 / 5 + 32)}°F
            </div>
            <div style={{ fontSize: '1.3em', color: theme.colors.TEXT_SECONDARY, marginBottom: 8 }}>
              {(() => {
                // Map weathercode to description
                const code = weather.weathercode;
                if (code === 0) return 'Clear';
                if (code === 1) return 'Mainly clear';
                if (code === 2) return 'Partly cloudy';
                if (code === 3) return 'Overcast';
                if (code >= 45 && code <= 48) return 'Fog';
                if (code >= 51 && code <= 57) return 'Drizzle';
                if (code >= 61 && code <= 67) return 'Rain';
                if (code >= 71 && code <= 77) return 'Snow';
                if (code >= 80 && code <= 86) return 'Showers';
                if (code >= 95 && code <= 99) return 'Thunderstorm';
                return 'Weather';
              })()}
            </div>
            <div style={{ fontSize: '1.2em', color: theme.colors.TEXT }}>
              Sunset: {(() => {
                const d = new Date(weather.sunset);
                return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              })()}
            </div>
          </>
        ) : null}
      </div>

      {/* Active Round Section */}
      {/* <section style={{
        backgroundColor: theme.colors.SURFACE,
        padding: theme.spacing.MEDIUM,
        borderRadius: '8px',
        marginBottom: theme.spacing.LARGE,
        border: `1px solid ${theme.colors.BORDER}`
      }}>
        <h2 style={{
          color: theme.colors.TEXT,
          marginBottom: theme.spacing.MEDIUM,
          fontSize: theme.fonts.LARGE
        }}>Active Round</h2>
        {(localRound && localRound.status === 'active') ? (
          <div style={{
            backgroundColor: theme.colors.BACKGROUND,
            padding: theme.spacing.MEDIUM,
            borderRadius: '4px',
            marginBottom: theme.spacing.SMALL,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: `1px solid ${theme.colors.BORDER}`
          }} className="card">
            <div style={{ textAlign: 'right', width: '100%' }}>
              <div style={{ marginBottom: theme.spacing.SMALL }}>
                <strong>{localRound.setup.courseName}</strong> &mdash; Tee: {localRound.setup.selectedTee}
              </div>
              <button
                onClick={() => {
                  // Navigate to hole entry for local round
                  window.location.href = `/hole-entry/${localRound.setup.courseId}/${localRound.id}/${localRound.setup.startingHole}`;
                }}
                style={{
                  padding: theme.spacing.MEDIUM,
                  backgroundColor: theme.colors.PRIMARY,
                  color: theme.colors.BACKGROUND,
                  border: 'none',
                  borderRadius: theme.borderRadius.MEDIUM,
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  marginBottom: theme.spacing.SMALL
                }}
              >
                Continue Round
              </button>
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete this round? This action cannot be undone.')) {
                    clearLocalRoundData();
                    window.location.reload();
                  }
                }}
                style={{
                  backgroundColor: theme.colors.ERROR,
                  color: theme.colors.BACKGROUND,
                  padding: theme.spacing.MEDIUM,
                  borderRadius: theme.borderRadius.MEDIUM,
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '1.1em',
                  fontWeight: 'bold'
                }}
              >
                Delete Round
              </button>
            </div>
          </div>
        ) : userDashboardModel?.activeRoundId ? (
          <div style={{
            backgroundColor: theme.colors.BACKGROUND,
            padding: theme.spacing.MEDIUM,
            borderRadius: '4px',
            marginBottom: theme.spacing.SMALL,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: `1px solid ${theme.colors.BORDER}`
          }} className="card">
            <div style={{ textAlign: 'right', width: '100%' }}>
              <button
                onClick={() => window.location.href = `/hole-entry?roundId=${userDashboardModel.activeRoundId}`}
                style={{
                  padding: theme.spacing.MEDIUM,
                  backgroundColor: theme.colors.PRIMARY,
                  color: theme.colors.BACKGROUND,
                  border: 'none',
                  borderRadius: theme.borderRadius.MEDIUM,
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  marginBottom: theme.spacing.SMALL
                }}
              >
                Continue Round
              </button>
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete this round? This action cannot be undone.')) {
                    // TODO: Implement Firestore round deletion and dashboard update if needed
                    clearLocalRoundData();
                    window.location.reload();
                  }
                }}
                style={{
                  backgroundColor: theme.colors.ERROR,
                  color: theme.colors.BACKGROUND,
                  padding: theme.spacing.MEDIUM,
                  borderRadius: theme.borderRadius.MEDIUM,
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '1.1em',
                  fontWeight: 'bold'
                }}
              >
                Delete Round
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: theme.colors.BACKGROUND,
            padding: theme.spacing.MEDIUM,
            borderRadius: '4px',
            marginBottom: theme.spacing.SMALL,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: `1px solid ${theme.colors.BORDER}`
          }} className="card">
            <div style={{ textAlign: 'center', width: '100%' }}>
              <p style={{ marginBottom: theme.spacing.MEDIUM }}>No active round</p>
              <button
                onClick={() => window.location.href = '/start-round'}
                style={{
                  padding: theme.spacing.MEDIUM,
                  backgroundColor: theme.colors.PRIMARY,
                  color: theme.colors.BACKGROUND,
                  border: 'none',
                  borderRadius: theme.borderRadius.MEDIUM,
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '1.1em',
                  fontWeight: 'bold'
                }}
              >
                Start A New Round
              </button>
            </div>
          </div>
        )}
      </section> */}

    </div>
  );
};

export default Dashboard;